import { memo, useMemo } from 'react';
import { Stack } from '@chakra-ui/react';
import { Control, UseFormWatch } from 'react-hook-form';

import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { Client } from '@/types/Client';
import { Job, JobBasicInfo } from '@/types/Job';
import { CandidateWithJobContext } from '@/types/Candidate';
import { FormValues } from '../hooks/useSessionForm';
import { DropdownField } from './DropdownField';

interface HierarchicalSelectionProps {
  control: Control<FormValues>;
  validationRules: {
    client: { required: unknown };
    job: { required: unknown };
    candidate: { required: unknown };
  };
  errors: Record<string, unknown>;
  isSubmitting: boolean;
  isLoadingCandidate: boolean;
  isUpdateMode: boolean;
  isFixedJobInterview?: boolean;
  clients: Client[] | null;
  jobs: JobBasicInfo[] | null;
  candidates: CandidateWithJobContext[] | null;
  selectedClient?: { value?: Client };
  selectedJob?: { value?: Job };
  watch: UseFormWatch<FormValues>;
  handleClientSelection: (client?: Client) => void;
  handleJobSelection: (job?: JobBasicInfo & { candidates?: CandidateWithJobContext[] }) => void;
}

export const HierarchicalSelection: React.FC<HierarchicalSelectionProps> = memo(
  ({
    control,
    validationRules,
    errors,
    isSubmitting,
    isLoadingCandidate,
    isUpdateMode,
    isFixedJobInterview = false,
    clients,
    jobs,
    candidates,
    selectedClient,
    selectedJob,
    watch,
    handleClientSelection,
    handleJobSelection
  }) => {
    // Memoize disabled states to prevent unnecessary recalculations
    const isFieldDisabled = useMemo(
      () => isUpdateMode || isFixedJobInterview,
      [isUpdateMode, isFixedJobInterview]
    );

    const isClientDisabled = useMemo(
      () => isSubmitting || isLoadingCandidate || clients?.length === 0 || isFieldDisabled,
      [isSubmitting, isLoadingCandidate, clients?.length, isFieldDisabled]
    );

    const isJobCandidateDisabled = useMemo(
      () => isSubmitting || isLoadingCandidate || isFieldDisabled,
      [isSubmitting, isLoadingCandidate, isFieldDisabled]
    );

    return (
      <Stack gap="24px" mt="24px">
        <DropdownField<Client, FormValues>
          name="client"
          label={<RequiredFieldLabel fontWeight="semibold">Client</RequiredFieldLabel>}
          control={control}
          rules={validationRules.client}
          error={errors.client as { message?: string }}
          disabled={isClientDisabled}
          placeholder="Select client"
          items={clients ?? []}
          getItemValue={(client) => client.client_id}
          getItemLabel={(client) => `${client.unique_id} - ${client.name}`}
          selectedValue={(watch('client') as { value?: Client })?.value?.client_id}
          isLoading={isLoadingCandidate}
          loadingText="Loading clients..."
          onSelectionChange={handleClientSelection}
        />

        {selectedClient && (
          <DropdownField<JobBasicInfo, FormValues>
            name="job"
            label={<RequiredFieldLabel fontWeight="semibold">Job</RequiredFieldLabel>}
            control={control}
            rules={validationRules.job}
            error={errors.job as { message?: string }}
            disabled={isJobCandidateDisabled}
            placeholder="Select job"
            items={jobs ?? []}
            getItemValue={(job) => job.job_id}
            getItemLabel={(job) => `${job.unique_id} - ${job.job_title}`}
            selectedValue={(watch('job') as { value?: Job })?.value?.job_id}
            onSelectionChange={handleJobSelection}
          />
        )}

        {selectedJob && (
          <DropdownField<CandidateWithJobContext, FormValues>
            name="candidate"
            label={<RequiredFieldLabel fontWeight="semibold">Candidate</RequiredFieldLabel>}
            control={control}
            rules={validationRules.candidate}
            error={errors.candidate as { message?: string }}
            disabled={isJobCandidateDisabled}
            placeholder="Select candidate"
            items={candidates ?? []}
            getItemValue={(candidate) => candidate.candidate_id}
            getItemLabel={(candidate) =>
              `${candidate.unique_id} - ${candidate.first_name} (${candidate.email})`
            }
            selectedValue={
              (watch('candidate') as { value?: CandidateWithJobContext })?.value?.candidate_id
            }
            width="100%"
          />
        )}
      </Stack>
    );
  }
);

HierarchicalSelection.displayName = 'HierarchicalSelection';
